import ModalComponent from '@components/modal/Modal.component';
import {useTranslation} from 'react-i18next';
import {Button, Stack} from '@mui/material';
import {ManageClientContract} from '@store/clients/contracts';
import AsyncSelectWithSearchComponent from '@components/inputs/asyncSelect/AsyncSelectWithSearch.component';
import {Sizes} from '@constants/sizes.constants';
import {FilterBaseEntityEnum, filtersService} from '@store/filters';
import {TableFilterType} from '@store/common/tableFilter.model';
import {finalize, map} from 'rxjs';
import React, {useState} from 'react';
import {SelectItem} from '@components/inputs/select/Select.component';
import {LoadingButton} from '@mui/lab';
import {clientsServicesService} from '@store/clients/services';
import {useSnackbar} from 'notistack';

interface UpdateWasteContainerModalProps {
  contract: ManageClientContract;
  handleClose: () => void;
}

const UpdateWasteContainerModal = (props: UpdateWasteContainerModalProps) => {
  const {contract, handleClose} = props;

  const {enqueueSnackbar} = useSnackbar();

  const [loading, setLoading] = useState(false);

  const [waste, setWaste] = useState<SelectItem>({
    value: contract?.services?.[0]?.waste?.id,
    label: contract?.services?.[0]?.wasteName || ''
  });
  const [container, setContainer] = useState<SelectItem>({
    value: contract?.services?.[0]?.container?.id,
    label: contract?.services?.[0]?.containerName || ''
  });

  const handleSave = () => {
    if (contract.id) {
      setLoading(true);
      clientsServicesService.updateWasteContainerForContainerId(contract.id, container.value, waste.value)
        .pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            handleClose();
          },
          error: (err) => enqueueSnackbar(err.text, err.options)
        });
    }
  };

  const {t} = useTranslation();

  return <ModalComponent
    aria-label="over-drawer"
    handleClose={handleClose}
    maxWidth="md"
    title={t('clients.contracts.updateWasteContainer.title')}
    content={
      <Stack pt="20px" pb="20px" width="100%" direction="column" spacing={2} overflow="hidden">
        <AsyncSelectWithSearchComponent
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('clients.details.contractedServices.manage.waste')}
          placeholder={t('global.selectOption')}
          handleChange={(waste) => setWaste(waste)}
          required={false}
          value={waste}
          color="secondary"
          getOptions={(search) => filtersService.getFilters(TableFilterType.WASTE, FilterBaseEntityEnum.NONE, [], search).pipe(
            map((options) => options.map((o) => ({label: o.name || '-', value: o.id})))
          )}/>
        <AsyncSelectWithSearchComponent
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('clients.details.contractedServices.manage.container')}
          placeholder={t('global.selectOption')}
          handleChange={(container) => setContainer(container)}
          required={false}
          color="secondary"
          value={container}
          getOptions={(search) => filtersService.getFilters(TableFilterType.CONTAINER, FilterBaseEntityEnum.NONE, [], search).pipe(
            map((options) => options.map((o) => ({label: o.name || '-', value: o.id})))
          )}/>
      </Stack>
    }
    actions={
      (
        <>
          <Button
            variant="outlined"
            onClick={handleClose}
            color="primary">
            {t('global.cancel')}
          </Button>
          <LoadingButton
            variant="contained"
            loading={loading}
            onClick={handleSave}
            color="primary">
            {t('global.save')}
          </LoadingButton>
        </>
      )
    }
  />;
};

export default UpdateWasteContainerModal;
